import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Button, Flex, Select, Typography } from '@percent/lemonade'
import { ExperimentQuestion, QuestionnaireQuestion } from './grantAdvisoryBooking.utils'
import styles from './GrantAdvisoryBookingModal.module.scss'

type Option = {
  value: string
  labelKey: string
  disqualifies?: boolean
}

const QUESTIONS: { question: ExperimentQuestion; questionKey: string; options: Option[] }[] = [
  {
    question: 'founded',
    questionKey: 'grantAdvisoryQuestionnaire.founded.question',
    options: [
      {
        value: 'less_than_year',
        labelKey: 'grantAdvisoryQuestionnaire.founded.options.lessThanYear',
        disqualifies: true
      },
      { value: 'one_to_three_years', labelKey: 'grantAdvisoryQuestionnaire.founded.options.oneToThree' },
      { value: 'more_than_three_years', labelKey: 'grantAdvisoryQuestionnaire.founded.options.moreThanThree' }
    ]
  },
  {
    question: 'revenue',
    questionKey: 'grantAdvisoryQuestionnaire.revenue.question',
    options: [
      { value: 'under_50k', labelKey: 'grantAdvisoryQuestionnaire.revenue.options.under50k', disqualifies: true },
      { value: '50k_500k', labelKey: 'grantAdvisoryQuestionnaire.revenue.options.fiftyToFiveHundred' },
      { value: '500k_1m', labelKey: 'grantAdvisoryQuestionnaire.revenue.options.fiveHundredToMillion' },
      { value: 'over_1m', labelKey: 'grantAdvisoryQuestionnaire.revenue.options.overMillion' }
    ]
  },
  {
    question: 'grantExperience',
    questionKey: 'grantAdvisoryQuestionnaire.grantExperience.question',
    options: [
      { value: 'won_before', labelKey: 'grantAdvisoryQuestionnaire.grantExperience.options.wonBefore' },
      { value: 'actively_looking', labelKey: 'grantAdvisoryQuestionnaire.grantExperience.options.activelyLooking' },
      {
        value: 'just_exploring',
        labelKey: 'grantAdvisoryQuestionnaire.grantExperience.options.justExploring',
        disqualifies: true
      }
    ]
  }
]

type QuestionnaireFormValues = Record<ExperimentQuestion, string>

const buildDefaultValues = (): QuestionnaireFormValues =>
  QUESTIONS.reduce((values, { question }) => ({ ...values, [question]: '' }), {} as QuestionnaireFormValues)

export type GrantQuestionnaireProps = {
  onStart: () => void
  // `disqualifying` is true whether or not it is acted on, so callers can measure it.
  onAnswer: (question: QuestionnaireQuestion, answer: string, disqualifying: boolean) => void
  onDisqualify: (reason: QuestionnaireQuestion) => void
  onQualified: () => void
  // Supplied by the caller, not read from PostHog here, so this stays presentational.
  // Empty in control, i.e. every question disqualifies as before.
  nonBlockingQuestions: readonly QuestionnaireQuestion[]
}

export const GrantQuestionnaire = ({
  onStart,
  onAnswer,
  onDisqualify,
  onQualified,
  nonBlockingQuestions
}: GrantQuestionnaireProps) => {
  const { t } = useTranslation()

  const translate = t as (key: string) => string
  const [step, setStep] = useState(1)
  const { control, watch, getValues } = useForm<QuestionnaireFormValues>({
    defaultValues: buildDefaultValues()
  })

  useEffect(() => {
    onStart()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const current = QUESTIONS[step - 1]
  const selectedValue = watch(current.question)

  const handleNext = () => {
    const option = current.options.find(({ value }) => value === getValues(current.question))
    if (!option) {
      return
    }

    onAnswer(current.question, option.value, Boolean(option.disqualifies))

    if (option.disqualifies && !nonBlockingQuestions.includes(current.question)) {
      onDisqualify(current.question)
      return
    }

    if (step >= QUESTIONS.length) {
      onQualified()
      return
    }

    setStep(step + 1)
  }

  const handleBack = () => setStep(prev => Math.max(1, prev - 1))

  return (
    <div className={styles.questionStep}>
      <div
        key={step}
        className={`${styles.stepBody} ${styles.stepEnter}`}
        data-testid={`grant-questionnaire-${current.question}`}
      >
        <Flex direction="column" gap={16}>
          <Typography variant="small" $color="gray500" className={styles.centeredText}>
            {t('grantAdvisoryQuestionnaire.progress', { current: step, total: QUESTIONS.length })}
          </Typography>
          <Typography variant="h5" className={styles.centeredText}>
            {translate(current.questionKey)}
          </Typography>
          <Controller
            name={current.question}
            control={control}
            render={({ field }) => {
              const fieldOption = current.options.find(({ value }) => value === field.value)

              return (
                <Select
                  data-testid={`grant-questionnaire-select-${current.question}`}
                  placeholder={t('grantAdvisoryQuestionnaire.selectPlaceholder')}
                  options={current.options.map(option => ({ value: option.value, label: translate(option.labelKey) }))}
                  defaultValue={
                    fieldOption ? { value: fieldOption.value, label: translate(fieldOption.labelKey) } : undefined
                  }
                  onChange={newValue => field.onChange(newValue.value)}
                />
              )
            }}
          />
        </Flex>
      </div>

      <Flex justify="space-between" gap={12} className={styles.stepFooter}>
        <Button
          variant="secondary"
          onClick={handleBack}
          disabled={step === 1}
          data-testid="grant-questionnaire-back-button"
        >
          {t('grantAdvisoryQuestionnaire.back')}
        </Button>
        <Button
          variant="primary"
          onClick={handleNext}
          disabled={!selectedValue}
          data-testid="grant-questionnaire-next-button"
        >
          {t('grantAdvisoryQuestionnaire.next')}
        </Button>
      </Flex>
    </div>
  )
}
